import React from 'react';

/* tint palette */
const PAL = {
  dine: { tint: '#eaf2ff', ring: 'ring-blue-100',   text: 'text-blue-600'   },
  take: { tint: '#f8f1ff', ring: 'ring-purple-100', text: 'text-purple-600' },
};

const Bell = ({ cls }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118.5 14.5V11
         a6.5 6.5 0 00-5-6.294V4a1.5 1.5 0 10-3 0v.706
         A6.5 6.5 0 005.5 11v3.5c0 .133-.052.261-.147.355L4 17h5m6 0v1a3 3 0 01-6 0v-1m6 0H9" />
  </svg>
);

const Chevron = ({ open }) => (
  <svg viewBox="0 0 20 20" className={`h-4 w-4 ${open ? 'rotate-180' : ''} transition-transform`} fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 8l4 4 4-4" strokeLinecap="round" />
  </svg>
);

const shortId = s => s?.slice(-6) ?? '';

export default function WaiterSection({
  dineIn,
  takeAway,
  expanded,
  onToggle,
  onDelivered,
  markDelivered,
}) {
  return (
    <div className="grid md:grid-cols-2 gap-6 flex-1 overflow-hidden">
      <Column
        title="Dine-In (Out for Delivery)"
        rows={dineIn}
        pal={PAL.dine}
        {...{ expanded, onToggle, onDelivered, markDelivered }}
      />
      <Column
        title="Take-Away (Out for Delivery)"
        rows={takeAway}
        pal={PAL.take}
        {...{ expanded, onToggle, onDelivered, markDelivered }}
      />
    </div>
  );
}

function Column({
  title,
  rows,
  pal,
  expanded,
  onToggle,
  onDelivered,
  markDelivered,
}) {
  return (
    <section className="flex flex-col rounded-2xl overflow-hidden bg-white ring-1 ring-black/5">
      <header className="px-4 py-3 border-b font-semibold">{title}</header>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-[#c7c9e0]/60">
        {rows.map(o => (
          <Card
            key={o._id}
            ord={o}
            pal={pal}
            open={expanded === o._id}
            toggle={() => onToggle(expanded === o._id ? null : o._id)}
            onDelivered={onDelivered}
            markDelivered={markDelivered}
          />
        ))}
        {rows.length === 0 && (
          <p className="pt-8 text-center text-gray-400 text-sm">— No orders —</p>
        )}
      </div>
    </section>
  );
}

function Card({ ord, pal, open, toggle, onDelivered, markDelivered }) {
  const deliver = async () => {
    await markDelivered(ord._id);
    onDelivered();
  };

  return (
    <article className={`rounded-xl ring-1 ${pal.ring} bg-white hover:shadow-lg transition-shadow`}>
      {/* header strip */}
      <div
        onClick={toggle}
        className="flex items-center px-4 py-[10px]"
        style={{
          background: pal.tint,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
        }}
      >
        <Bell cls={`h-5 w-5 ${pal.text}`} />
        <p className={`ml-2 font-medium ${pal.text}`}>#{shortId(ord.order_serial_no)}</p>
        <div className="ml-auto h-7 w-7 flex items-center justify-center rounded-full bg-white ring-1 ring-black/10">
          <Chevron open={open} />
        </div>
      </div>

      {/* meta */}
      <div className="px-4 pt-2 pb-3 text-sm">
        {ord.dining_table_id && (
          <>
            Table <b>{ord.dining_table_id.name}</b>
            <br />
          </>
        )}
        {ord.token && (
          <>
            Token <b>{ord.token}</b>
            <br />
          </>
        )}
        <span className="text-xs text-gray-500">
          {new Date(ord.order_datetime).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
          ,{' '}
          {new Date(ord.order_datetime).toLocaleDateString()}
        </span>
      </div>

      {/* collapse */}
      <div
        className={`grid transition-all duration-300 overflow-hidden ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="min-h-0">
          <div className="divide-y">
            {ord.order_items?.map(it => (
              <div key={it._id} className="flex gap-2 py-3 text-sm px-4">
                <span className="w-8 font-semibold">{it.quantity}×</span>
                <div className="flex-1">
                  <p className="font-medium">{it.item_id?.name}</p>
                  {it.size && (
                    <p className="text-xs text-gray-500">Size: {it.size}</p>
                  )}
                </div>
              </div>
            ))}

            <div className="p-4">
              <button
                onClick={deliver}
                className="w-full h-10 rounded-md bg-[#12B76A] text-white font-medium hover:opacity-90 transition"
              >
                Mark Delivered
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
